import { TableContainer } from "@/components/table-container";
import { DynamicForm } from "@/components/dynamic-form";
import { getInitialValues } from "@/components/dynamic-form/helper/getInitialValues";
import { useDynamicForm } from "@/components/dynamic-form/hook/useDynamicForm";
import { Table } from "@renderbus/ui";
import styled from "styled-components";
import { columns } from "./config/table-columns";
import { getChildManageSearchConfig } from "./config/child-manage-search-config";
import { ISubAccountData, useSubAccountStore } from "@/store/SubAccountStore";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import { AddChildModal } from "@/pages/child-customer/view/child-manage/components/add-child-modal";
import { useMemo, useState } from "react";
import useForm from "antd/es/form/hooks/useForm";
import request from "project-config/services/request/request";
import { AddButton, PlusIcon, SearchAreaWrapper } from "../../atom";
import { useGlobalStore } from "@/store/GlobalStore";
import { message } from "antd";
import { MaxSubaccountModal } from "./components/max-subaccount-modal";
import { useTranslation } from "react-i18next";
import { ColumnType } from "antd/es/table";
import { Whether } from "project-config/constants";

export interface ISubAccountForm {
  subUserName: string;
  subPassword: string;
  phone: string;
  email: string;
  status: number;
}

export function ChildManage() {
  const {
    subAccountList,
    fetchSubAccountList,
    isMaxSubAccount,
    setSubUserNameQuery,
  } = useSubAccountStore();
  const searchConfig = useMemo(() => getChildManageSearchConfig(), []);
  const { form: searchForm, getSearchParams } = useDynamicForm({
    api: "/api/rendering/user/querySubUserList",
    noPagination: true,
  });
  const t = getTranslateNsT("center");
  const { t: errorT } = useTranslation("error");
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openMaxAccountModal, setOpenMaxAccountModal] = useState(false);
  const [form] = useForm<ISubAccountForm>();
  const { isDomestic, isCertified, isPersonal, userInfo } = useGlobalStore();
  function handleSubmit() {
    setLoading(true);
    form
      .validateFields()
      .then(async () => {
        const { code } = await request.post("/api/rendering/user/addSubUser", {
          subUserName: form.getFieldValue("subUserName"),
          subPassword: form.getFieldValue("subPassword"),
          phone: form.getFieldValue("phone") || "",
          email: form.getFieldValue("email")
            ? form.getFieldValue("email").toLowerCase()
            : "",
          status: form.getFieldValue("status")
            ? +form.getFieldValue("status")
            : 0,
        });
        setLoading(false);
        if (code === 200) {
          message.success(t("user_com_childManage_add_success_msg"));
          form.resetFields();
          void fetchSubAccountList();
        } else {
          message.error(errorT(`error_${code}`));
          form.resetFields();
        }
        setOpen(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }

  const _columns = useMemo(
    () =>
      columns.map((column: ColumnType<ISubAccountData>) => {
        if (column.dataIndex === "cloudCoupon") {
          return {
            ...column,
            hidden: userInfo?.enableRemoteDesktop === Whether.No,
          };
        }
        return column;
      }),
    [userInfo?.enableRemoteDesktop]
  );

  return (
    <ChildManageTableContainer>
      <SearchAreaWrapper>
        <AddButton
          type="primary"
          icon={<PlusIcon />}
          disabled={isDomestic && (!isCertified || isPersonal)}
          onClick={() => {
            isMaxSubAccount ? setOpenMaxAccountModal(true) : setOpen(true);
          }}
        >
          {t("user_com_childManage_btn_add_sub")}
        </AddButton>
        <DynamicForm
          initialValues={getInitialValues(searchConfig)}
          searchConfig={searchConfig}
          searchForm={searchForm}
          onSearch={() => {
            const raw = getSearchParams().userName;
            setSubUserNameQuery(
              raw === undefined || raw === null ? "" : String(raw).trim()
            );
          }}
          showButtonIcon
          clearBtn={<></>}
        />
      </SearchAreaWrapper>
      <Table
        id="child-customer-table"
        columns={_columns}
        dataSource={subAccountList}
        hidePaginationOnSinglePage={true}
        pagination={false}
      />
      <MaxSubaccountModal
        open={openMaxAccountModal}
        modalProps={{
          onCancel: () => setOpenMaxAccountModal(false),
        }}
      />
      <AddChildModal
        open={open}
        form={form}
        modalProps={{
          onCancel: () => {
            setOpen(false);
            setLoading(false);
            form.resetFields();
          },
          onOk: handleSubmit,
          okButtonProps: { loading },
        }}
      />
    </ChildManageTableContainer>
  );
}

const ChildManageTableContainer = styled(TableContainer)`
  padding-bottom: 100px;
`;
